import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gem, Search, ArrowUpDown, X, Clapperboard, Heart, Share2, ArrowRight, ArrowLeft,
  Film, Layers, Play, Lock, Users, Globe, Mic, UserRound, ChevronDown, UserPlus, UsersRound, Check,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { getWisdomMoments, updateMomentVisibility, tagPersonInMoment, untagPersonInMoment, searchPeople } from '../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const FONT_A = "'Caveat', cursive";

const AVATAR_COLORS_WISDOM = ['#556B2F', '#A63C00', '#5B3F7A', '#9A4122', '#2F5A8A', '#6E531A', '#4E6B1C', '#9A3A5B'];
const avatarColorWisdom = (name) => AVATAR_COLORS_WISDOM[(name || '').charCodeAt(0) % AVATAR_COLORS_WISDOM.length];

const CAT_THEME = {
  life_advice:         { label: 'Life Advice',         bg: '#E9F0DC', fg: '#3E4F22', dot: '#6B8E23' },
  family_history:      { label: 'Family History',      bg: '#F1EBE0', fg: '#7A5C2E', dot: '#8B7355' },
  cultural_tradition:  { label: 'Cultural Tradition',  bg: '#FBEBD6', fg: '#8A4B00', dot: '#C08A2E' },
  personal_story:      { label: 'Personal Story',      bg: '#E7EFF8', fg: '#2E5C8A', dot: '#3B72C4' },
  blessing:            { label: 'Blessing',            bg: '#EFE6F4', fg: '#5B3F7A', dot: '#7B6B8E' },
  humor:               { label: 'Humor',               bg: '#F9EAF0', fg: '#9A3A5B', dot: '#B0506B' },
  other:               { label: 'Other',               bg: '#F0EFE7', fg: '#5A6150', dot: '#828A76' },
};

const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const avatarColor = (name) => AVATAR_COLORS[name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0];

const VIS_CONFIG = {
  private: { icon: Lock, label: 'Only me', color: '#9A3A5B' },
  close:   { icon: Users, label: 'Close family', color: '#9A6A0A' },
  family:  { icon: Globe, label: 'Whole family', color: '#48611F' },
};

/* ─── Visibility Indicator (read-only) ─── */
function VisIndicator({ visibility }) {
  const cfg = VIS_CONFIG[visibility] || VIS_CONFIG.family;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: cfg.color, fontFamily: FONT_B, fontWeight: 600 }}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function fmtTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

/* ─── Category Badge ─── */
function CatBadge({ category }) {
  const t = CAT_THEME[category] || CAT_THEME.other;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: t.bg, color: t.fg, fontWeight: 700, fontSize: 12.5,
      padding: '5px 12px', borderRadius: 999, fontFamily: FONT_B, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.dot }} />
      {t.label}
    </span>
  );
}

/* ─── Child Badge ─── */
function ChildBadge() {
  return <span style={{ background: '#FBEFD8', color: '#9A6A0A', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, fontFamily: FONT_B, marginLeft: 4 }}>child</span>;
}

/* ─── Searchable Dropdown Picker ─── */
function PersonPicker({ icon: Icon, label, value, valueName, items, onSelect, searchFn, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const timer = setTimeout(() => {
      searchFn(query).then(data => { setResults(data.people || []); setLoading(false); }).catch(() => setLoading(false));
    }, query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [open, query, searchFn]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <button onClick={() => { setOpen(!open); setQuery(''); }} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        background: '#fff', border: '1.5px solid #E4E1D2', borderRadius: 999,
        padding: '10px 15px', cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
      }}>
        <Icon size={15} color="#9AA088" />
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#9AA088', letterSpacing: '.5px', textTransform: 'uppercase' }}>{label}</span>
        {value ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: avatarColor(valueName), color: '#fff', fontWeight: 700, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(valueName || '?')[0]}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#2C2F26' }}>{valueName}</span>
          </span>
        ) : (
          <span style={{ fontSize: 14, color: '#5A6150' }}>{placeholder}</span>
        )}
        <ChevronDown size={15} color="#9AA088" style={{ marginLeft: 'auto' }} />
      </button>

      {open && <>
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 14 }} />
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 300,
          background: '#fff', border: '1px solid #E4E1D2', borderRadius: 16,
          boxShadow: '0 18px 48px rgba(40,46,28,.22)', zIndex: 15, overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #F0EEE3' }}>
            <div style={{ position: 'relative' }}>
              <Search size={15} color="#9AA088" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 10, border: '1px solid #E4E1D2', fontSize: 14, fontFamily: FONT_B, outline: 'none' }}
                onFocus={e => { e.target.style.borderColor = '#6B8E23'; }} onBlur={e => { e.target.style.borderColor = '#E4E1D2'; }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {/* Reset row */}
            <div onClick={() => { onSelect(null, null); setOpen(false); }} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
              background: !value ? '#FAFAF3' : 'transparent',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
              onMouseLeave={e => { e.currentTarget.style.background = !value ? '#FAFAF3' : 'transparent'; }}
            >
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#EDEBDD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={14} color="#828A76" /></div>
              <span style={{ fontSize: 14.5, fontWeight: 600, color: '#2C2F26', fontFamily: FONT_B }}>{placeholder}</span>
              {!value && <Check size={16} color="#556B2F" style={{ marginLeft: 'auto' }} />}
            </div>
            {/* Results */}
            {results.map(p => (
              <div key={p.id} onClick={() => { onSelect(p.id, p.name); setOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: avatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 600, color: '#2C2F26', fontFamily: FONT_B }}>{p.name}</span>
                  {p.is_minor && <ChildBadge />}
                </div>
                {value === p.id && <Check size={16} color="#556B2F" />}
              </div>
            ))}
            {!loading && results.length === 0 && query && (
              <div style={{ padding: '16px 14px', textAlign: 'center', fontSize: 14, color: '#9AA088', fontFamily: FONT_B }}>No matches</div>
            )}
          </div>
        </div>
      </>}
    </div>
  );
}

/* ─── Person Tag Editor (in modal) ─── */
function PersonTagEditor({ moment, onTag, onUntag }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  const featured = moment.featured_people || [];

  useEffect(() => {
    if (!pickerOpen) return;
    const timer = setTimeout(() => {
      searchPeople(tagQuery).then(data => {
        // Exclude already-tagged
        const taggedIds = new Set(featured.map(p => p.id));
        setResults((data.people || []).filter(p => !taggedIds.has(p.id)));
      }).catch(() => {});
    }, tagQuery ? 250 : 0);
    return () => clearTimeout(timer);
  }, [pickerOpen, tagQuery, featured]);

  useEffect(() => { if (pickerOpen && inputRef.current) inputRef.current.focus(); }, [pickerOpen]);

  return (
    <div style={{ border: '1px solid #EAE7D8', background: '#FBFBF3', borderRadius: 14, padding: '15px 17px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <UsersRound size={16} color="#556B2F" />
        <span style={{ fontWeight: 700, fontSize: 14.5, color: '#2C2F26', fontFamily: FONT_B }}>Who's in this moment</span>
      </div>
      <p style={{ fontSize: 13, color: '#9AA088', fontFamily: FONT_B, margin: '0 0 12px' }}>Tag the people it's about, so it can be found by name.</p>

      {/* Tagged chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {featured.map(p => (
          <span key={p.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff',
            border: '1px solid #E8E7D8', borderRadius: 999, padding: '5px 10px 5px 5px', fontSize: 13, fontFamily: FONT_B,
          }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: avatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</span>
            <span style={{ fontWeight: 600, color: '#2C2F26' }}>{p.name}</span>
            {p.is_minor && <ChildBadge />}
            <button onClick={() => onUntag(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 2px', marginLeft: 2, borderRadius: '50%', display: 'flex' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F5EAEE'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            ><X size={13} color="#9A3A5B" /></button>
          </span>
        ))}

        {/* Tag someone button */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setPickerOpen(!pickerOpen); setTagQuery(''); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
            border: '1.5px dashed #C4CBB4', borderRadius: 999, padding: '7px 14px',
            color: '#556B2F', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FONT_B, minHeight: 38,
          }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F4F7EC'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
          >
            <UserPlus size={14} /> Tag someone
          </button>

          {pickerOpen && <>
            <div onClick={() => setPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 14 }} />
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 280,
              background: '#fff', border: '1px solid #E4E1D2', borderRadius: 14,
              boxShadow: '0 18px 48px rgba(40,46,28,.22)', zIndex: 15, overflow: 'hidden',
            }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #F0EEE3' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={14} color="#9AA088" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                  <input ref={inputRef} value={tagQuery} onChange={e => setTagQuery(e.target.value)}
                    placeholder="Search family & children..."
                    style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 10, border: '1px solid #E4E1D2', fontSize: 13, fontFamily: FONT_B, outline: 'none' }}
                  />
                </div>
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {results.map(p => (
                  <div key={p.id} onClick={() => { onTag(p.id); setPickerOpen(false); }} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', cursor: 'pointer',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 30, height: 30, borderRadius: '50%', background: avatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#2C2F26', fontFamily: FONT_B }}>{p.name}</span>
                    {p.is_minor && <ChildBadge />}
                  </div>
                ))}
                {results.length === 0 && (
                  <div style={{ padding: '14px', textAlign: 'center', fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>No one left to tag</div>
                )}
              </div>
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}

/* ─── Moment Card ─── */
function MomentCard({ moment, onClick, onPersonFilter }) {
  const sp = moment.speaker;
  const cat = CAT_THEME[moment.category] || CAT_THEME.other;

  return (
    <div onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()} style={{
      background: '#fff', border: '1px solid #E4E1D2', borderRadius: 18, padding: '22px 24px 18px',
      boxShadow: '0 6px 22px rgba(40,46,28,.07)', cursor: 'pointer',
      display: 'flex', flexDirection: 'column', transition: 'transform .15s, box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 16px 38px rgba(40,46,28,.14)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(40,46,28,.07)'; }}
    >
      {/* Said by speaker line */}
      {sp && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {sp.kind === 'guest' ? (
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: '#EFEDE3',
              border: '2px dashed #7F846F', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><UserRound size={18} color="#3F4436" /></div>
          ) : (
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: avatarColorWisdom(sp.name),
              color: '#fff', fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{sp.initial}</div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#4A4F40', fontFamily: FONT_B }}>
              {sp.kind === 'guest' ? 'Said by a guest' : 'Said by'}
            </div>
            <div style={{ fontSize: 16, fontFamily: FONT_B, color: '#2C2F26' }}>
              {sp.kind === 'guest' ? (
                <span style={{ fontWeight: 700 }}>{sp.name || 'Unnamed guest'}</span>
              ) : (
                <span style={{ fontWeight: 700 }}>{sp.name}</span>
              )}
              {sp.kind !== 'guest' && <span style={{ fontWeight: 500, color: '#4A4F40' }}> · Family</span>}
              {sp.kind === 'guest' && <span style={{ fontWeight: 500, color: '#4A4F40' }}> · not in the family tree</span>}
            </div>
          </div>
        </div>
      )}

      {/* Quote */}
      <p style={{
        fontFamily: FONT_H, fontWeight: 500, fontStyle: 'italic', fontSize: 19, lineHeight: 1.5,
        color: '#2C2F26', margin: '0 0 18px', flex: 1,
        overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical',
      }}>
        {moment.original_text}
      </p>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #E4E1D2', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <CatBadge category={moment.category} />
        {moment.video?.story_title && (
          <span style={{ fontSize: 14, fontWeight: 600, color: '#3E4F22', fontFamily: FONT_B }}>
            {moment.video.story_title}
            {moment.start_ms != null && ` · ${Math.floor(moment.start_ms / 60000)}:${String(Math.floor((moment.start_ms % 60000) / 1000)).padStart(2, '0')}`}
          </span>
        )}
      </div>

      {/* "For ..." featured people row */}
      {moment.featured_people?.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#B0A98E', letterSpacing: '.5px', textTransform: 'uppercase', fontFamily: FONT_B }}>FOR</span>
          {moment.featured_people.map(p => (
            <span key={p.id} onClick={e => { e.stopPropagation(); onPersonFilter && onPersonFilter(p.id, p.name); }} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F4F5EC', border: '1px solid #E8E7D8',
              borderRadius: 999, padding: '3px 10px 3px 3px', fontSize: 12.5, fontFamily: FONT_B, cursor: 'pointer',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#EEF0E2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F4F5EC'; }}
            >
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: avatarColor(p.name), color: '#fff', fontWeight: 700, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.initial}</span>
              {p.name?.split(' ')[0]}
              {p.is_minor && <ChildBadge />}
            </span>
          ))}
        </div>
      )}

      {/* Source line + visibility */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>
        {moment.video?.story_title && <>
          <Clapperboard size={13} />
          <span style={{ color: '#556B2F', fontWeight: 600 }}>{moment.video.story_title}</span>
          <span>·</span>
          <span>{fmtTime(moment.start_ms)}</span>
        </>}
        <span style={{ marginLeft: 'auto' }}><VisIndicator visibility={moment.visibility} /></span>
      </div>
    </div>
  );
}

/* ─── Moment Detail Modal ─── */
function MomentModal({ moment, allMoments, onClose, onSelect, navigate, onVisibilityChange, onMomentUpdate }) {
  if (!moment) return null;
  const sp = moment.speaker;

  // Related moments
  const moreSpeaker = sp ? allMoments.filter(m => m.id !== moment.id && m.speaker?.id === sp.id).slice(0, 3) : [];
  const moreCat = allMoments.filter(m => m.id !== moment.id && m.category === moment.category && m.speaker?.id !== sp?.id).slice(0, 3);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(28,32,22,.55)', backdropFilter: 'blur(3px)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px',
      overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 22, maxWidth: 720, width: '100%',
        boxShadow: '0 30px 80px rgba(24,28,16,.4)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #F0EEE3' }}>
          <CatBadge category={moment.category} />
          <button onClick={onClose} style={{ width: 38, height: 38, borderRadius: '50%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F2F1E7'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            <X size={20} color="#5A6150" />
          </button>
        </div>

        {/* Video placeholder */}
        {moment.video?.story_id && (
          <div style={{
            height: 200, background: 'linear-gradient(135deg,#2E3524,#4A5A2E)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', position: 'relative',
          }}>
            <button onClick={() => navigate(`/stories/${moment.video.story_id}`)} style={{
              width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,.92)',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Play size={28} color="#556B2F" style={{ marginLeft: 4 }} />
            </button>
            <div style={{ position: 'absolute', bottom: 14, left: 18, display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.85)', fontSize: 13.5, fontFamily: FONT_B }}>
              <Clapperboard size={14} /> {moment.video.story_title} · Clip {fmtTime(moment.start_ms)} – {fmtTime(moment.end_ms)}
            </div>
          </div>
        )}

        <div style={{ padding: '28px 32px 32px' }}>
          {/* Quote */}
          <span style={{ fontFamily: FONT_H, fontSize: 56, lineHeight: '36px', color: '#D9C79A' }}>"</span>
          <p style={{ fontFamily: FONT_H, fontWeight: 500, fontStyle: 'italic', fontSize: 25, lineHeight: 1.45, color: '#2C2F26', margin: '0 0 24px' }}>
            {moment.original_text}
          </p>

          {/* Speaker */}
          {sp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #F0EEE3' }}>
              <div style={{
                width: 50, height: 50, borderRadius: '50%', background: avatarColor(sp.name),
                color: '#fff', fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer',
              }} onClick={() => navigate(`/wisdom/speaker/${sp.id}`)}>{sp.initial}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: '#2C2F26', fontFamily: FONT_B }}>{sp.name}</div>
                <div style={{ fontSize: 14, color: '#9AA088', fontFamily: FONT_B }}>
                  shared {moment.created_at ? new Date(moment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </div>
              </div>
              <span onClick={() => navigate(`/wisdom/speaker/${sp.id}`)} style={{ fontSize: 14, fontWeight: 700, color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>
                All from {sp.name.split(' ')[0]} <ArrowRight size={14} style={{ verticalAlign: '-2px' }} />
              </span>
            </div>
          )}

          {/* Who's in this moment — people tagging */}
          <PersonTagEditor
            moment={moment}
            onTag={async (personId) => {
              try {
                await tagPersonInMoment(moment.id, personId);
                const pData = await searchPeople('', 100);
                const person = (pData.people || []).find(p => p.id === personId);
                if (person) {
                  const updated = { ...moment, featured_people: [...(moment.featured_people || []), person] };
                  onSelect(updated);
                  if (onMomentUpdate) onMomentUpdate(updated);
                }
              } catch (e) { console.error('Tag failed:', e); }
            }}
            onUntag={async (personId) => {
              try {
                await untagPersonInMoment(moment.id, personId);
                const updated = { ...moment, featured_people: (moment.featured_people || []).filter(p => p.id !== personId) };
                onSelect(updated);
                if (onMomentUpdate) onMomentUpdate(updated);
              } catch (e) { console.error('Untag failed:', e); }
            }}
          />

          {/* Visibility control */}
          <div style={{ marginBottom: 20, padding: '14px 18px', background: '#FAFAF3', borderRadius: 12, border: '1px solid #EFEEE5' }}>
            {moment.is_owner ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#5A6150', fontFamily: FONT_B, marginBottom: 8 }}>Who can see this moment</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(VIS_CONFIG).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    const active = moment.visibility === key;
                    return (
                      <button key={key} onClick={() => onVisibilityChange(moment.id, key)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999,
                        border: active ? `2px solid ${cfg.color}` : '1.5px solid #E4E1D2',
                        background: active ? '#fff' : '#FAFAF3', color: active ? cfg.color : '#5A6150',
                        fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FONT_B, minHeight: 38,
                      }}>
                        <Icon size={14} /> {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <VisIndicator visibility={moment.visibility} />
                <span style={{ fontSize: 13, fontStyle: 'italic', color: '#828A76', fontFamily: FONT_B }}>
                  — Only {sp?.name?.split(' ')[0] || 'the speaker'} can change who sees this moment.
                </span>
              </div>
            )}
          </div>

          {/* Tags */}
          {moment.tags?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {moment.tags.map(tag => (
                <span key={tag} style={{ background: '#F2F1E7', color: '#5A6150', fontSize: 13, fontWeight: 600, padding: '5px 12px', borderRadius: 999, fontFamily: FONT_B }}>#{tag}</span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <button style={{
              display: 'flex', alignItems: 'center', gap: 6, background: '#556B2F', color: '#fff',
              fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
            }}>
              <Heart size={16} /> Save
            </button>
            <button style={{
              display: 'flex', alignItems: 'center', gap: 6, background: '#fff', color: '#556B2F',
              fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: '1px solid #C4CBB4', cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
            }}>
              <Share2 size={16} /> Share
            </button>
            {moment.video?.story_id && (
              <button onClick={() => navigate(`/stories/${moment.video.story_id}`)} style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: '#fff', color: '#556B2F',
                fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: '1px solid #C4CBB4', cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
              }}>
                Open full story <ArrowRight size={14} />
              </button>
            )}
          </div>

          {/* More from speaker */}
          {moreSpeaker.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26', margin: '0 0 12px' }}>More from {sp?.name.split(' ')[0]}</h4>
              {moreSpeaker.map(m => (
                <div key={m.id} onClick={() => onSelect(m)} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
                  borderBottom: '1px solid #F0EEE3', cursor: 'pointer',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: (CAT_THEME[m.category] || CAT_THEME.other).dot, marginTop: 7, flexShrink: 0 }} />
                  <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontSize: 15, color: '#33372B', margin: 0, flex: 1, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {m.original_text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* More in category */}
          {moreCat.length > 0 && (
            <div>
              <h4 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26', margin: '0 0 12px' }}>More {(CAT_THEME[moment.category] || CAT_THEME.other).label}</h4>
              {moreCat.map(m => (
                <div key={m.id} onClick={() => onSelect(m)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                  borderBottom: '1px solid #F0EEE3', cursor: 'pointer',
                }}>
                  {m.speaker && (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: avatarColor(m.speaker.name), color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{m.speaker.initial}</div>
                  )}
                  <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontSize: 15, color: '#33372B', margin: 0, flex: 1, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {m.original_text}
                  </p>
                  {m.speaker && <span style={{ fontSize: 13, color: '#9AA088', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>{m.speaker.name.split(' ')[0]}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Wisdom Library Page ─── */
export default function WisdomLibrary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [speakerFilter, setSpeakerFilter] = useState('all');
  const [speakerName, setSpeakerName] = useState('');
  const [personFilter, setPersonFilter] = useState('all');
  const [personName, setPersonName] = useState('');
  const [selMoment, setSelMoment] = useState(null);

  const fetchMoments = useCallback(() => {
    setLoading(true);
    const params = {};
    if (cat !== 'all') params.category = cat;
    if (speakerFilter !== 'all') params.speaker_id = speakerFilter;
    if (personFilter !== 'all') params.person_id = personFilter;
    if (q.trim()) params.q = q.trim();
    getWisdomMoments(params)
      .then(setData)
      .catch(() => setData({ moments: [], stats: { total: 0, speaker_count: 0, category_counts: {} }, speakers: [], result_count: 0 }))
      .finally(() => setLoading(false));
  }, [q, cat, speakerFilter, personFilter]);

  useEffect(() => { fetchMoments(); }, [fetchMoments]);

  // Close modal on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelMoment(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const moments = data?.moments || [];
  const stats = data?.stats || { total: 0, speaker_count: 0, category_counts: {} };
  const speakers = data?.speakers || [];
  const hasFilters = cat !== 'all' || speakerFilter !== 'all' || personFilter !== 'all' || q.trim();

  // Most popular category
  const topCat = Object.entries(stats.category_counts || {}).sort((a, b) => b[1] - a[1])[0];
  const topCatLabel = topCat ? (CAT_THEME[topCat[0]] || CAT_THEME.other).label : '—';

  return (
    <HubLayout activeView="wisdom">
      <div className="wl-content" style={{ maxWidth: 1040, margin: '0 auto', padding: '34px 32px 90px' }}>

        {/* Treasure header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginBottom: 28 }}>
          <div style={{
            width: 66, height: 66, borderRadius: 15, flexShrink: 0,
            background: 'linear-gradient(140deg, #8E6B23, #B79046)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 18px rgba(142,107,35,.3)',
          }}>
            <Gem size={30} color="#FFF6E6" />
          </div>
          <div>
            <div style={{ fontFamily: FONT_A, fontSize: 22, color: '#B5760A', marginBottom: 2 }}>A family treasure</div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 33, color: '#2C2F26', margin: 0, lineHeight: 1.1 }}>Family Wisdom</h1>
            <p style={{ fontFamily: FONT_B, fontSize: 16, color: '#5A6150', marginTop: 4, maxWidth: 620 }}>
              Meaningful quotes, advice, and stories spoken by your family — preserved from video recordings.
            </p>
          </div>
        </div>

        {/* Stats strip */}
        <div style={{
          background: 'linear-gradient(180deg, #FBFBF1, #F4F5E7)', border: '1px solid #E4E1D2',
          borderRadius: 16, display: 'flex', marginBottom: 24, overflow: 'hidden',
        }}>
          {[
            { value: stats.total, label: 'Wisdom moments' },
            { value: stats.speaker_count, label: 'Voices preserved' },
            { value: topCatLabel, label: 'Most shared theme', isText: true },
          ].map((s, i) => (
            <div key={i} style={{
              flex: 1, padding: '18px 24px', textAlign: 'center',
              borderRight: i < 2 ? '1px solid #E4E1D2' : 'none',
            }}>
              <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: s.isText ? 20 : 27, color: '#556B2F' }}>{s.value}</div>
              <div style={{ fontSize: 13.5, color: '#7C846A', fontFamily: FONT_B, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search + sort */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} color="#9AA088" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search quotes, summaries, keywords..."
              style={{
                width: '100%', padding: '14px 18px 14px 44px', borderRadius: 999,
                border: '1px solid #E4E1D2', fontSize: 15, fontFamily: FONT_B, color: '#2C2F26',
                background: '#fff', outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = '#6B8E23'; e.target.style.boxShadow = '0 0 0 3px rgba(107,142,35,.14)'; }}
              onBlur={e => { e.target.style.borderColor = '#E4E1D2'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <button style={{
            display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #E4E1D2',
            borderRadius: 999, padding: '0 18px', fontFamily: FONT_B, fontSize: 14, fontWeight: 600, color: '#5A6150', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <ArrowUpDown size={15} /> Newest first
          </button>
        </div>

        {/* Category chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={() => setCat('all')} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 999,
            border: cat === 'all' ? 'none' : '1.5px solid #E4E1D2', fontWeight: 700, fontSize: 13.5,
            background: cat === 'all' ? '#556B2F' : '#fff', color: cat === 'all' ? '#fff' : '#5A6150',
            cursor: 'pointer', fontFamily: FONT_B, minHeight: 38,
          }}>All moments <span style={{ opacity: .7 }}>{stats.total}</span></button>

          {Object.entries(CAT_THEME).map(([key, t]) => {
            const count = stats.category_counts?.[key] || 0;
            if (count === 0) return null;
            const active = cat === key;
            return (
              <button key={key} onClick={() => setCat(active ? 'all' : key)} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999,
                border: active ? 'none' : '1.5px solid #E4E1D2', fontWeight: 700, fontSize: 13.5,
                background: active ? '#556B2F' : '#fff', color: active ? '#fff' : '#5A6150',
                cursor: 'pointer', fontFamily: FONT_B, minHeight: 38,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#fff' : t.dot }} />
                {t.label} <span style={{ opacity: .7 }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Voice + Featured dropdown pickers */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 22 }}>
          <PersonPicker
            icon={Mic} label="Voice"
            value={speakerFilter === 'all' ? null : speakerFilter}
            valueName={speakerName}
            onSelect={(id, name) => { setSpeakerFilter(id || 'all'); setSpeakerName(name || ''); }}
            searchFn={(q) => searchPeople(q)}
            placeholder="Everyone"
          />
          <PersonPicker
            icon={UserRound} label="Featured"
            value={personFilter === 'all' ? null : personFilter}
            valueName={personName}
            onSelect={(id, name) => { setPersonFilter(id || 'all'); setPersonName(name || ''); }}
            searchFn={(q) => searchPeople(q)}
            placeholder="Anyone"
          />
        </div>

        {/* Result meta + clear */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontSize: 14, color: '#828A76', fontFamily: FONT_B }}>
            {loading ? 'Loading...' : `${moments.length} moment${moments.length !== 1 ? 's' : ''}`}
          </span>
          {hasFilters && (
            <button onClick={() => { setCat('all'); setSpeakerFilter('all'); setSpeakerName(''); setPersonFilter('all'); setPersonName(''); setQ(''); }} style={{
              display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
              color: '#556B2F', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: FONT_B,
            }}>
              <X size={14} /> Clear filters
            </button>
          )}
        </div>

        {/* Card grid or empty state */}
        {!loading && moments.length === 0 ? (
          <div style={{
            border: '1.5px dashed #D8D3BE', borderRadius: 20, padding: '60px 40px', textAlign: 'center',
            background: 'repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(216,211,190,.06) 10px, rgba(216,211,190,.06) 20px)',
          }}>
            <div style={{
              width: 66, height: 66, borderRadius: 15, margin: '0 auto 20px',
              background: 'linear-gradient(140deg, #8E6B23, #B79046)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Gem size={28} color="#FFF6E6" />
            </div>
            <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#2C2F26', margin: '0 0 8px' }}>
              {hasFilters ? 'No moments match yet' : 'The treasure chest is empty'}
            </h3>
            <p style={{ fontSize: 16, color: '#5A6150', fontFamily: FONT_B, maxWidth: 440, margin: '0 auto 20px' }}>
              {hasFilters
                ? 'Try adjusting your filters or search to find more moments.'
                : "Upload videos of family elders speaking — our AI will find and preserve their wisdom automatically."
              }
            </p>
            <button onClick={() => hasFilters ? (setCat('all'), setSpeakerFilter('all'), setSpeakerName(''), setPersonFilter('all'), setPersonName(''), setQ('')) : navigate('/tell-a-story')} style={{
              background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15,
              padding: '12px 28px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 48,
            }}>
              {hasFilters ? 'Clear all filters' : 'Record with an elder'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="wl-grid">
            {moments.map(m => (
              <MomentCard key={m.id} moment={m} onClick={() => setSelMoment(m)} onPersonFilter={(id, name) => { setPersonFilter(id); setPersonName(name); }} />
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="wl-grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{ background: '#fff', border: '1px solid #E4E1D2', borderRadius: 18, padding: '22px 24px', height: 220 }}>
                <div style={{ width: '60%', height: 14, background: '#EFEEE5', borderRadius: 8, marginBottom: 12 }} />
                <div style={{ width: '100%', height: 14, background: '#EFEEE5', borderRadius: 8, marginBottom: 8 }} />
                <div style={{ width: '80%', height: 14, background: '#EFEEE5', borderRadius: 8, marginBottom: 8 }} />
                <div style={{ width: '40%', height: 14, background: '#EFEEE5', borderRadius: 8 }} />
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 700px) { .wl-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 640px) { .wl-content { padding: 16px 16px 60px !important; } }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>

      {/* Detail modal */}
      {selMoment && (
        <MomentModal
          moment={selMoment}
          allMoments={moments}
          onClose={() => setSelMoment(null)}
          onSelect={m => setSelMoment(m)}
          navigate={navigate}
          onVisibilityChange={async (momentId, newVis) => {
            try {
              await updateMomentVisibility(momentId, { visibility: newVis });
              setSelMoment(prev => prev ? { ...prev, visibility: newVis } : prev);
              setData(prev => prev ? {
                ...prev,
                moments: prev.moments.map(m => m.id === momentId ? { ...m, visibility: newVis } : m),
              } : prev);
            } catch (e) { console.error('Visibility update failed:', e); }
          }}
          onMomentUpdate={(updated) => {
            setData(prev => prev ? {
              ...prev,
              moments: prev.moments.map(m => m.id === updated.id ? updated : m),
            } : prev);
          }}
        />
      )}
    </HubLayout>
  );
}
